import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Icon } from '@cbrebuild/blocks';
import './external-voucher-nav-control.scss';
 
/**
* ExternalVoucherNavControl
* Displays a banner similar to proxy banner when the active deal is restricted.
* Sits directly beneath the proxy banner in main navigation.
*/
const ExternalVoucherNavControl = ({ dealOverride }) => {
  // Attempt to get deal details from redux; fallback to dealOverride if provided
  const reduxDeal = useSelector(state => state.dealDetails?.deal || state.deal?.deal || {});
  const location = useLocation();
  // Prefer explicit override when provided; otherwise redux value.
  const deal = dealOverride || reduxDeal || {};
  const isDealRestricted = useMemo(() => (deal && deal.is_deal_restricted), [deal]);

  // Only show on specific voucher workflow tabs: getting started, voucher details (lease details), financials,
  // comp (lease comp), review & send, confirmation, and voucher overview (within voucher_details route).
  // Routes where the banner SHOULD NOT appear (exclusion list). Keep prefixes descriptive & minimal.
  const excludedRoutePrefixes = [
    '/profile/teams',
    '/profile/overview',
    '/profile/labs',
    '/profile/switch-proxy',
    '/spoc-search',
    '/vouchers',
    '/pipeline',
    '/commissions/annual_chart',
    '/commissions/installments',
    '/commissions/my_payments',
    '/properties',
    '/my-contacts',
    '/tasks',
    '/deals/executing',
    '/deals/closed',
    '/deals/prospects',
    '/profile/manage-proxy'
  ];
  const isExcluded = excludedRoutePrefixes.some(p => location.pathname.startsWith(p));

  if (!isDealRestricted || isExcluded) return null;
 
  return (
    <div className="external-voucher-nav-control-container" role="status" aria-live="polite">
      {/* Removed hyperlink/NavLink wrapper to satisfy requirement: banner should not redirect when clicked */}
      <div className="external-voucher-control-text external-voucher-control-active" aria-label="Connector voucher notice">
        <span className="external-voucher-inline">
          <Icon iconName="info-circle" />
          <span className="external-voucher-label" style={{ fontWeight: 600 }}>ConnectorPro Vouchers/Early Invoices must be submitted in ConnectorPro. Deal IQ edits will be overwritten. Contact Connector Support (<a href="mailto:connectorsupport@cbre.com">connectorsupport@cbre.com</a>) for issues.</span>
        </span>
      </div>
    </div>
  );
};
 
ExternalVoucherNavControl.propTypes = {
  dealOverride: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    is_deal_restricted: PropTypes.bool,
  }),
};
 
ExternalVoucherNavControl.defaultProps = {
  dealOverride: undefined,
};
 
export default ExternalVoucherNavControl;